/**
 * Arbre des prérequis affiché dans la popup « Que voulez-vous travailler ? »
 * (plan popup-choix-attendu, phase 2). Module pur : il lit le graphe rendu par
 * `GET /themes/liens-prerequis/` (`{ "<attendu_id>": [prérequis bloquants…] }`).
 */

const enfants = (graphe, id, garder) => (graphe[id] || []).filter(garder);
const tous = () => true;

/** Largeur d'abord : chaque descendant de `racine` (elle comprise, parent
 * `null`) avec le premier parent qui lui donne sa profondeur la plus courte. */
const parents = (racine, graphe, garder = tous) => {
  const parent = new Map([[racine, null]]);
  let front = [racine];
  while (front.length) {
    const suivant = [];
    front.forEach((id) => enfants(graphe, id, garder).forEach((p) => {
      if (!parent.has(p)) { parent.set(p, id); suivant.push(p); }
    }));
    front = suivant;
  }
  return parent;
};

/**
 * Les lignes de l'arbre d'un attendu, `[{ id, profondeur }]`, dans l'ordre des
 * branches (parcours en profondeur, enfants dans l'ordre du graphe). Un
 * prérequis atteint par plusieurs chemins n'apparaît qu'une fois, à sa
 * profondeur la plus courte, sous le premier parent qui la lui donne (même
 * règle qu'`arbre_de` côté serveur). `garder(id)` écarte un prérequis et
 * tout ce qui n'est atteint que par lui.
 */
export const lignesArbre = (racine, graphe, garder = tous) => {
  const parent = parents(racine, graphe, garder);
  // Profondeur d'abord, en ne descendant que vers les enfants retenus.
  const lignes = [];
  const visiter = (id, profondeur) => {
    lignes.push({ id, profondeur });
    enfants(graphe, id, garder).forEach((p) => { if (parent.get(p) === id) visiter(p, profondeur + 1); });
  };
  visiter(racine, 0);
  return lignes;
};

/**
 * Le fil de l'arbre affiché (popup, 13/10/2026) : pour chaque ligne, un
 * morceau par colonne de retrait, de 1 à sa profondeur : `branche` (├, un
 * frère suit), `fin` (└, dernier enfant), `passe` (│, la branche d'un ancêtre
 * continue plus bas) ou `vide`. La racine n'en a aucun (son tronc revient à
 * l'affichage). Calculé sur les lignes AFFICHÉES : une ligne sautée (prérequis
 * non chargé) rattache ses descendants à la ligne du dessus, sans fil coupé ni
 * fil qui pend.
 */
export const filsDeLArbre = (lignes) => {
  const profondeurs = [];
  lignes.forEach(({ profondeur }, i) => profondeurs.push(i === 0 ? 0 : Math.min(profondeur, profondeurs[i - 1] + 1)));
  // La branche de profondeur k continue sous la ligne i si la première ligne
  // plus bas qui remonte à k ou au-dessus est à k.
  const suite = (i, k) => {
    for (let j = i + 1; j < profondeurs.length; j += 1) {
      if (profondeurs[j] <= k) return profondeurs[j] === k;
    }
    return false;
  };
  return profondeurs.map((d, i) => Array.from({ length: d }, (_, c) => {
    const continuee = suite(i, c + 1);
    if (c + 1 === d) return continuee ? 'branche' : 'fin';
    return continuee ? 'passe' : 'vide';
  }));
};

/**
 * Le graphe réduit aux arbres de `racines` : chaque racine et chacun de ses
 * descendants, avec ses liens ; tout autre attendu n'a plus de prérequis
 * (13/10/2026 : seuls les attendus à arbre actif montrent leurs prérequis,
 * les autres restent proposés seuls). Les arbres des racines sont inchangés.
 */
export const grapheDesArbres = (graphe, racines) => {
  const garde = {};
  const pile = [...racines];
  while (pile.length) {
    const id = pile.pop();
    if (Object.prototype.hasOwnProperty.call(garde, id)) continue;
    garde[id] = graphe[id] || [];
    garde[id].forEach((p) => pile.push(p));
  }
  return garde;
};

/** Les attendus de `ids` qui ne sont prérequis (même indirect) d'aucun autre. */
const racines = (ids, graphe) => {
  const dessous = new Set();
  ids.forEach((id) => parents(id, graphe).forEach((_, p) => { if (p !== id) dessous.add(p); }));
  return ids.filter((id) => !dessous.has(id));
};

/**
 * Les résultats d'une recherche à afficher en tête, chacun avec son arbre.
 * `groupes` : `[{ niveau, ids }]`, le niveau de la classe d'abord, puis les
 * niveaux précédents du plus proche au plus lointain (comportement F1) : seul
 * le premier qui a des résultats compte. Aucun résultat : `null`.
 */
export const racinesDeRecherche = (groupes, graphe) => {
  const groupe = groupes.find((g) => g.ids.length > 0);
  return groupe ? { niveau: groupe.niveau, ids: racines(groupe.ids, graphe) } : null;
};
